import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { api, getApiErrorMessage } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function formatDateTime(value) {
  return formatApiDateTimeDisplay(undefined, value)
}

function toTime(value) {
  if (!value) return 0
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? t : 0
}

function hasHighSignal(card) {
  const signals = Array.isArray(card?.signals) ? card.signals : []
  return signals.some((signal) => signal?.severity === 'HIGH')
}

function earliestSignalTime(card) {
  const signals = Array.isArray(card?.signals) ? card.signals : []
  const times = signals.map((signal) => toTime(signal?.sortAt)).filter(Boolean)
  return times.length > 0 ? Math.min(...times) : Number.MAX_SAFE_INTEGER
}

function recentActivityTime(card) {
  return Math.max(
    toTime(card?.updatedAt),
    toTime(card?.pipelineStageUpdatedAt),
    toTime(card?.createdAt),
  )
}

function stageEnteredTime(card) {
  return toTime(card?.pipelineStageUpdatedAt) || toTime(card?.createdAt) || toTime(card?.updatedAt)
}

function sortPipelineItems(items, mode) {
  const sorted = [...items]
  sorted.sort((a, b) => {
    if (mode === 'name') {
      return String(a?.customerName || '').localeCompare(String(b?.customerName || ''), 'ko')
    }
    if (mode === 'recent') {
      return recentActivityTime(b) - recentActivityTime(a)
    }
    if (mode === 'stale') {
      return stageEnteredTime(a) - stageEnteredTime(b)
    }
    const highDelta = Number(hasHighSignal(b)) - Number(hasHighSignal(a))
    if (highDelta !== 0) return highDelta
    const dueDelta = earliestSignalTime(a) - earliestSignalTime(b)
    if (dueDelta !== 0) return dueDelta
    return recentActivityTime(b) - recentActivityTime(a)
  })
  return sorted
}

function cloneBoardData(board) {
  if (!board || typeof board !== 'object') return board
  return {
    ...board,
    columns: Array.isArray(board.columns)
      ? board.columns.map((column) => ({ ...column, items: Array.isArray(column.items) ? [...column.items] : [] }))
      : [],
    managedCustomers: Array.isArray(board.managedCustomers) ? [...board.managedCustomers] : [],
    managementEndedCustomers: Array.isArray(board.managementEndedCustomers) ? [...board.managementEndedCustomers] : [],
  }
}

function removeCardFromBoard(next, customerRefId) {
  let found = null
  next.columns = next.columns.map((column) => {
    const items = []
    for (const card of (Array.isArray(column.items) ? column.items : [])) {
      if (String(card?.customerRefId) === String(customerRefId)) {
        found = card
      } else {
        items.push(card)
      }
    }
    return { ...column, items }
  })
  next.managedCustomers = (next.managedCustomers || []).filter((card) => {
    if (String(card?.customerRefId) === String(customerRefId)) {
      found = found || card
      return false
    }
    return true
  })
  next.managementEndedCustomers = (next.managementEndedCustomers || []).filter((card) => {
    if (String(card?.customerRefId) === String(customerRefId)) {
      found = found || card
      return false
    }
    return true
  })
  return found
}

function recomputeBoardStats(columns) {
  const counts = Object.fromEntries(BOARD_STAGE_KEYS.map((stage) => {
    const column = (columns || []).find((item) => item?.stage === stage)
    return [stage, Array.isArray(column?.items) ? column.items.length : 0]
  }))
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0)
  let remaining = total
  return BOARD_STAGE_KEYS.map((stage) => {
    const count = counts[stage] || 0
    const row = {
      stage,
      label: pipelineStageLabel(stage),
      count,
      shareRate: total > 0 ? Math.round((count * 1000) / total) / 10 : 0,
      onwardCount: remaining,
      conversionRate: total > 0 ? Math.round((remaining * 1000) / total) / 10 : 0,
    }
    remaining -= count
    return row
  })
}

function optimisticMoveBoardData(board, customerRefId, stage, reason = '') {
  const next = cloneBoardData(board)
  if (!next) return next
  const found = removeCardFromBoard(next, customerRefId)
  if (!found) return next
  const now = new Date().toISOString()
  const card = {
    ...found,
    pipelineStage: stage === 'MANAGEMENT_ENDED' ? 'MANAGEMENT_ENDED' : stage,
    pipelineStageLabel: stage === 'MANAGEMENT_ENDED' ? '관리 종료' : pipelineStageLabel(stage),
    pipelineStageManual: true,
    pipelineStageUpdatedAt: stage === 'MANAGEMENT_ENDED' ? found.pipelineStageUpdatedAt : now,
    managementEndedAt: stage === 'MANAGEMENT_ENDED' ? now : null,
    managementEndReason: stage === 'MANAGEMENT_ENDED' ? ((reason || '').trim() || null) : null,
  }
  if (stage === 'MANAGEMENT_ENDED') {
    next.managementEndedCustomers = [card, ...(next.managementEndedCustomers || [])]
  } else if (stage === 'MANAGED') {
    next.managedCustomers = [card, ...(next.managedCustomers || [])]
  } else {
    next.columns = next.columns.map((column) => (
      column.stage === stage ? { ...column, items: [card, ...(column.items || [])] } : column
    ))
  }
  next.stageStats = recomputeBoardStats(next.columns)
  return next
}

function categoryLabel(v) {
  if (v === 'MATURITY') return '만기'
  if (v === 'LEAD') return '리드'
  if (v === 'CUSTOMER_EVENT') return '이벤트'
  if (v === 'ANNIVERSARY') return '기념일'
  if (v === 'DORMANT_CUSTOMER') return '미연락'
  if (v === 'PROFILE_NEED') return '정보'
  if (v === 'COVERAGE_NEED') return '보장'
  if (v === 'CLAIM') return '청구'
  if (v === 'NEW_CUSTOMER') return '신규'
  if (v === 'RECONTACT') return '재응답'
  if (v === 'CONSULT_FOLLOWUP') return '후속'
  if (v === 'CONTRACT_LINK_NEED') return '연결'
  return v || '업무'
}

function signalBadgeText(signal) {
  return signal?.label || categoryLabel(signal?.category)
}

function uniqueSignalBadges(signals) {
  const out = []
  const indexByKey = new Map()
  for (const signal of Array.isArray(signals) ? signals : []) {
    const text = signalBadgeText(signal)
    const key = String(text || signal?.category || '').trim().toLowerCase()
    if (!key) continue
    const existingIndex = indexByKey.get(key)
    if (existingIndex == null) {
      indexByKey.set(key, out.length)
      out.push({ ...signal, label: text })
      continue
    }
    if (signal?.severity === 'HIGH' && out[existingIndex]?.severity !== 'HIGH') {
      out[existingIndex] = { ...out[existingIndex], ...signal, label: text, severity: 'HIGH' }
    }
  }
  return out
}

const QUEUE_TABS = [
  { key: 'contactNeeded', label: '연락 필요 고객', empty: '오늘 연락이 필요한 고객 업무가 없습니다.' },
  { key: 'recontactNeeded', label: '재응답 필요', empty: '재응답이 필요한 연락 기록이 없습니다.' },
  { key: 'consultationFollowups', label: '상담 후 후속 연락', empty: '상담 후 후속 연락이 필요한 고객이 없습니다.' },
  { key: 'staleContacts', label: '장기 미연락', empty: '최근 30일 기준 장기 미연락 고객이 없습니다.' },
  { key: 'newCustomers', label: '신규 고객', empty: '첫 연락이 필요한 신규 고객이 없습니다.' },
  { key: 'anniversaryEvents', label: '기념일·상령일', empty: '오늘 도래한 기념일·상령일이 없습니다.' },
  { key: 'profileNeeds', label: '필요고객', empty: '정보 보강이 필요한 고객이 없습니다.' },
  { key: 'contractLinkNeeds', label: '계약 연결 보강', empty: '계약 연결 보강이 필요한 만기 계약이 없습니다.' },
  { key: 'coverageNeeds', label: '보장분석 필요', empty: '보장분석이 필요한 고객이 없습니다.' },
  { key: 'claimInProgress', label: '청구 진행중', empty: '진행 중인 청구 업무가 없습니다.' },
  { key: 'maturityPolicies', label: '30일 이내 만기 계약', empty: '30일 이내 만기 계약이 없습니다.' },
  { key: 'contactHistory', label: '연락 기록', empty: '최근 30일 연락 기록이 없습니다.' },
]

const QUEUE_SECTIONS = [
  {
    title: '연락 카테고리',
    description: '지금 고객에게 연락하면 좋은 항목',
    keys: ['contactNeeded', 'recontactNeeded', 'consultationFollowups', 'staleContacts', 'newCustomers', 'anniversaryEvents'],
  },
  {
    title: '업무 카테고리',
    description: '설계사가 처리하거나 보강할 내부 업무',
    keys: ['profileNeeds', 'contractLinkNeeds', 'coverageNeeds', 'claimInProgress', 'maturityPolicies'],
  },
  {
    title: '이력 카테고리',
    description: '최근 발생한 고객 접점 기록',
    keys: ['contactHistory'],
  },
]

const PIPELINE_STAGE_OPTIONS = [
  { stage: 'LEAD', label: '잠재' },
  { stage: 'CONSULTING', label: '상담' },
  { stage: 'PROPOSAL', label: '제안' },
  { stage: 'JOINED', label: '가입' },
  { stage: 'RENEWAL', label: '갱신' },
  { stage: 'HOLD', label: '보류' },
  { stage: 'MANAGED', label: '관리' },
]
const PIPELINE_MANAGEMENT_ENDED_OPTION = { stage: 'MANAGEMENT_ENDED', label: '관리 종료' }
const PIPELINE_CARD_STAGE_OPTIONS = [...PIPELINE_STAGE_OPTIONS, PIPELINE_MANAGEMENT_ENDED_OPTION]

const PIPELINE_SORT_OPTIONS = [
  { value: 'urgency', label: '긴급도' },
  { value: 'recent', label: '최근 활동' },
  { value: 'stale', label: '오래 머문 순' },
  { value: 'name', label: '이름순' },
]

const PIPELINE_STAGE_LABELS = Object.fromEntries(PIPELINE_CARD_STAGE_OPTIONS.map((option) => [option.stage, option.label]))
const BOARD_STAGE_KEYS = ['LEAD', 'CONSULTING', 'PROPOSAL', 'JOINED', 'RENEWAL', 'HOLD']

function pipelineStageLabel(stage) {
  return PIPELINE_STAGE_LABELS[stage] || stage || ''
}

function findCardInBoard(board, customerRefId) {
  if (!board || !customerRefId) return null
  const groups = [
    ...(Array.isArray(board.columns) ? board.columns.map((column) => column.items) : []),
    board.managedCustomers,
    board.managementEndedCustomers,
  ]
  for (const group of groups) {
    const found = (Array.isArray(group) ? group : []).find((card) => String(card?.customerRefId) === String(customerRefId))
    if (found) return found
  }
  return null
}

const TABS_BY_KEY = Object.fromEntries(QUEUE_TABS.map((item) => [item.key, item]))

const NAV_LINK_CLASS =
  'inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50'
const REFRESH_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50'

export default function CustomerWorkQueuePage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [tab, setTab] = useState('contactNeeded')
  const [viewMode, setViewMode] = useState('list')
  const [boardData, setBoardData] = useState(null)
  const [boardLoading, setBoardLoading] = useState(false)
  const [boardErr, setBoardErr] = useState('')
  const [managedOpen, setManagedOpen] = useState(false)
  const [stageNoteTarget, setStageNoteTarget] = useState(null)
  const [managementEndTarget, setManagementEndTarget] = useState(null)

  const load = async () => {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get('/customer-management/work-queue', { __silent: true })
      setData(unwrap(res) || {})
    } catch (e) {
      setErr(getApiErrorMessage(e, '고객업무 허브를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const loadBoard = async ({ silent = false } = {}) => {
    if (!silent) {
      setBoardLoading(true)
      setBoardErr('')
    }
    try {
      const res = await api.get('/customer-management/work-queue/board', { __silent: true })
      setBoardData(unwrap(res) || {})
    } catch (e) {
      if (!silent) setBoardErr(getApiErrorMessage(e, '고객 보드를 불러오지 못했습니다.'))
    } finally {
      if (!silent) setBoardLoading(false)
    }
  }

  useEffect(() => {
    if (viewMode === 'board' && !boardData && !boardLoading) loadBoard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode])

  const movePipelineStage = async (customerRefId, stage) => {
    if (!customerRefId || !stage) return
    const previousBoard = cloneBoardData(boardData)
    const movedCard = findCardInBoard(boardData, customerRefId)
    setBoardData((prev) => optimisticMoveBoardData(prev, customerRefId, stage))
    if (stage !== 'MANAGED') setManagedOpen(false)
    try {
      await api.patch(`/customer-management/customers/${encodeURIComponent(customerRefId)}/pipeline-stage`, { stage }, { __silent: true })
      loadBoard({ silent: true })
      if (stage !== 'MANAGEMENT_ENDED') {
        setStageNoteTarget({
          customerRefId,
          customerName: movedCard?.customerName || '',
          stage,
          stageLabel: pipelineStageLabel(stage),
        })
      }
      try { window?.toast?.show?.('관리 단계가 변경되었습니다.', 'success') } catch { }
    } catch (e) {
      if (previousBoard) setBoardData(previousBoard)
      const msg = getApiErrorMessage(e, '관리 단계를 변경하지 못했습니다.')
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  }

  const endManagement = async (customerRefId, reason = '') => {
    if (!customerRefId) return false
    const previousBoard = cloneBoardData(boardData)
    setBoardData((prev) => optimisticMoveBoardData(prev, customerRefId, 'MANAGEMENT_ENDED', reason))
    try {
      await api.patch(
        `/customer-management/customers/${encodeURIComponent(customerRefId)}/pipeline-stage`,
        { stage: 'MANAGEMENT_ENDED', reason: (reason || '').trim() || null },
        { __silent: true },
      )
      loadBoard({ silent: true })
      try { window?.toast?.show?.('관리 종료 처리되었습니다.', 'success') } catch { }
      return true
    } catch (e) {
      if (previousBoard) setBoardData(previousBoard)
      const msg = getApiErrorMessage(e, '관리 종료 처리에 실패했습니다.')
      try { window?.toast?.show?.(msg, 'error') } catch { }
      return false
    }
  }

  const requestManagementEnd = (card) => {
    if (card?.customerRefId) setManagementEndTarget(card)
  }

  const confirmManagementEnd = async (reason) => {
    if (!managementEndTarget?.customerRefId) return
    const ok = await endManagement(managementEndTarget.customerRefId, reason)
    if (ok) setManagementEndTarget(null)
  }

  const saveStageMoveNote = async (note) => {
    const target = stageNoteTarget
    const normalized = (note || '').trim()
    if (!target || !normalized) {
      setStageNoteTarget(null)
      return
    }
    try {
      await api.post(
        `/customer-management/customers/${encodeURIComponent(target.customerRefId)}/pipeline-stage-note`,
        { stage: target.stage, note: normalized },
        { __silent: true },
      )
      setStageNoteTarget(null)
      try { window?.toast?.show?.('이동 메모가 저장되었습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '이동 메모를 저장하지 못했습니다.')
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  }

  const counts = useMemo(() => {
    const out = {}
    for (const t of QUEUE_TABS) out[t.key] = Array.isArray(data?.[t.key]) ? data[t.key].length : 0
    return out
  }, [data])
  const active = TABS_BY_KEY[tab] || QUEUE_TABS[0]
  const rows = Array.isArray(data?.[active.key]) ? data[active.key] : []

  return (
    <div className="py-2">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">고객업무 허브</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              className={`rounded-md px-3 py-1.5 text-sm font-semibold ${viewMode === 'list' ? 'bg-brand-navy text-white' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => setViewMode('list')}
            >
              목록으로 보기
            </button>
            <button
              type="button"
              className={`rounded-md px-3 py-1.5 text-sm font-semibold ${viewMode === 'board' ? 'bg-brand-navy text-white' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => setViewMode('board')}
            >
              보드로 보기
            </button>
          </div>
          <Link className={NAV_LINK_CLASS} to="/support/customers">
            고객관리
          </Link>
          <Link className={NAV_LINK_CLASS} to="/support/maturity-ledger">
            보험만기대장
          </Link>
          <button
            type="button"
            className={REFRESH_BUTTON_CLASS}
            onClick={viewMode === 'board' ? loadBoard : load}
            disabled={viewMode === 'board' ? boardLoading : loading}
          >
            새로고침
          </button>
        </div>
      </div>

      {err ? (
        <ErrorState description={err} onAction={load} className="mb-4 rounded-xl border border-rose-100 bg-white py-6" />
      ) : null}

      {viewMode === 'board' ? (
        <CustomerPipelineBoard
          data={boardData}
          loading={boardLoading}
          error={boardErr}
          onMove={movePipelineStage}
          onRequestEnd={requestManagementEnd}
          onRefresh={loadBoard}
          onOpenManaged={() => setManagedOpen(true)}
        />
      ) : (
        <>
      <div className="mb-5 grid gap-3 lg:grid-cols-3">
        {QUEUE_SECTIONS.map((section) => (
          <section key={section.title} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-end justify-between gap-3">
              <div>
                <div className="text-sm font-black text-slate-900">{section.title}</div>
                {/* <div className="mt-0.5 text-xs text-slate-500">{section.description}</div> */}
              </div>
              <div className="text-2xl font-black tabular-nums text-slate-900">
                {section.keys.reduce((sum, key) => sum + (counts[key] || 0), 0)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {section.keys.map((key) => {
                const item = TABS_BY_KEY[key]
                return (
                  <button
                    key={key}
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-left transition ${
                      tab === key
                        ? 'border-brand-sky bg-sky-50 text-brand-navy ring-1 ring-brand-sky/30'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-white'
                    }`}
                    onClick={() => setTab(key)}
                  >
                    <div className="truncate text-xs font-semibold">{item.label}</div>
                    <div className="mt-1 text-xl font-black tabular-nums">{counts[key] || 0}</div>
                  </button>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-900">{active.label}</div>
            </div>
            <div className="text-xs font-medium text-slate-500">{data?.date || ''}</div>
          </div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">불러오는 중...</div>
        ) : rows.length === 0 ? (
          <EmptyState title={active.empty} className="py-10" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse text-sm">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-3 text-left font-semibold">상태</th>
                  <th className="px-4 py-3 text-left font-semibold">제목</th>
                  <th className="px-4 py-3 text-left font-semibold">내용</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-semibold">일시</th>
                  <th className="px-4 py-3 text-right font-semibold">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row, idx) => (
                  <QueueRow key={`${active.key}-${row.id || row.maturityPolicyId || row.calendarEventId || row.anniversaryId || idx}`} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
        </>
      )}

      {managedOpen ? (
        <ManagedCustomersModal
          data={boardData}
          onClose={() => setManagedOpen(false)}
          onMove={movePipelineStage}
          onRequestEnd={requestManagementEnd}
          onEnd={endManagement}
        />
      ) : null}
      {stageNoteTarget ? (
        <StageMoveNotePrompt
          target={stageNoteTarget}
          onClose={() => setStageNoteTarget(null)}
          onSave={saveStageMoveNote}
        />
      ) : null}
      {managementEndTarget ? (
        <ManagementEndDialog
          target={managementEndTarget}
          onClose={() => setManagementEndTarget(null)}
          onEnd={confirmManagementEnd}
        />
      ) : null}
    </div>
  )
}

function CustomerPipelineBoard({
  data,
  loading,
  error,
  onMove,
  onRequestEnd,
  onRefresh,
  onOpenManaged,
}) {
  const columns = Array.isArray(data?.columns) ? data.columns : []
  const stageStats = Array.isArray(data?.stageStats) ? data.stageStats : []
  const managedCount = Array.isArray(data?.managedCustomers) ? data.managedCustomers.length : 0
  const endedCount = Array.isArray(data?.managementEndedCustomers) ? data.managementEndedCustomers.length : 0
  const [sortMode, setSortMode] = useState('urgency')
  const [activeDragCard, setActiveDragCard] = useState(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
  )

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">보드를 불러오는 중...</div>
  }
  if (error) {
    return <ErrorState description={error} onAction={onRefresh} className="rounded-xl border border-rose-100 bg-white py-6" />
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-black text-slate-900">고객 파이프라인 보드</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
          >
            {PIPELINE_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button type="button" className={NAV_LINK_CLASS} onClick={onOpenManaged}>
            관리 고객 {managedCount}{endedCount > 0 ? ` · 종료 ${endedCount}` : ''}
          </button>
          <button type="button" className={REFRESH_BUTTON_CLASS} onClick={onRefresh}>
            새로고침
          </button>
        </div>
      </div>

      {stageStats.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-6">
          {stageStats.map((row) => (
            <div key={row.stage} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-bold text-slate-500">{row.label}</div>
                <div className="text-lg font-black tabular-nums text-slate-900">{row.count ?? 0}</div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                <span>비중 {row.shareRate ?? 0}%</span>
                <span>전환 {row.conversionRate ?? 0}%</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        onDragStart={({ active }) => setActiveDragCard(active?.data?.current?.card || null)}
        onDragCancel={() => setActiveDragCard(null)}
        onDragEnd={({ over }) => {
          const card = activeDragCard
          const nextStage = over?.id
          setActiveDragCard(null)
          if (card?.customerRefId && nextStage && nextStage !== card.pipelineStage) {
            onMove(card.customerRefId, nextStage)
          }
        }}
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          {columns.map((column) => (
            <PipelineDroppableColumn
              key={column.stage}
              column={column}
              items={sortPipelineItems(Array.isArray(column.items) ? column.items : [], sortMode)}
              onMove={onMove}
              onRequestEnd={onRequestEnd}
              activeCustomerId={activeDragCard?.customerRefId}
            />
          ))}
        </div>
        <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
          {activeDragCard ? <PipelineCustomerCard card={activeDragCard} onMove={onMove} onRequestEnd={onRequestEnd} overlay /> : null}
        </DragOverlay>
      </DndContext>
    </section>
  )
}

function PipelineDroppableColumn({ column, items, onMove, onRequestEnd, activeCustomerId }) {
  const { isOver, setNodeRef } = useDroppable({ id: column.stage })
  return (
    <div
      ref={setNodeRef}
      className={`flex h-[calc(100vh-18rem)] min-h-[38rem] w-[18rem] shrink-0 flex-col rounded-2xl border bg-slate-50 transition ${
        isOver ? 'border-brand-sky ring-2 ring-brand-sky/30' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
        <div className="text-sm font-black text-slate-900">{column.label}</div>
        <div className="rounded-full bg-white px-2 py-0.5 text-xs font-bold tabular-nums text-slate-600">{items.length}</div>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2.5">
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white/70 px-3 py-8 text-center text-xs text-slate-400">비어 있음</div>
        ) : (
          items.map((card) => (
            <DraggablePipelineCustomerCard
              key={card.customerRefId}
              card={card}
              onMove={onMove}
              onRequestEnd={onRequestEnd}
              isActive={String(activeCustomerId || '') === String(card.customerRefId)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function DraggablePipelineCustomerCard({ card, onMove, onRequestEnd, isActive }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `customer-${card.customerRefId}`,
    data: { card },
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging || isActive ? 0.35 : 1,
    touchAction: 'none',
  }
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <PipelineCustomerCard card={card} onMove={onMove} onRequestEnd={onRequestEnd} />
    </div>
  )
}

function PipelineCustomerCard({ card, onMove, onRequestEnd, overlay = false }) {
  const signals = uniqueSignalBadges(card?.signals).slice(0, 4)
  const currentStage = card?.pipelineStage || 'MANAGED'
  const isManagementEnded = currentStage === 'MANAGEMENT_ENDED'
  const managementEndedAt = formatDateTime(card?.managementEndedAt)
  return (
    <article
      className={`rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${overlay ? 'w-[18rem] rotate-1 cursor-grabbing shadow-2xl' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link className="block truncate text-sm font-black text-slate-900 hover:text-brand-navy" to={card.targetPath || `/support/customers/${card.customerRefId}`}>
            {card.customerName || '고객'}
          </Link>
          <div className="mt-0.5 text-xs text-slate-500">{card.customerPhone || '-'}</div>
        </div>
        {isManagementEnded ? (
          <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700">관리 종료</span>
        ) : card.pipelineStageManual ? (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">수동</span>
        ) : null}
      </div>
      {isManagementEnded ? (
        <div className="mt-2 rounded-lg border border-rose-100 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700">
          <div>{managementEndedAt ? `관리 종료 · ${managementEndedAt}` : '관리 종료'}</div>
          {card.managementEndReason ? (
            <div className="mt-1 font-medium text-rose-700/80">{card.managementEndReason}</div>
          ) : null}
        </div>
      ) : null}
      {card.recommendedStageDiffers ? (
        <div className="mt-2 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
          {card.recommendedStageLabel} 추천
        </div>
      ) : null}
      {signals.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {signals.map((signal, idx) => (
            <span
              key={`${card.customerRefId}-${String(signal.label || signal.category || idx).trim()}-${idx}`}
              className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                signal.severity === 'HIGH' ? 'bg-rose-50 text-rose-700' : 'bg-sky-50 text-brand-navy'
              }`}
            >
              {signal.label}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-3 text-xs text-slate-400">당장 필요한 업무 없음</div>
      )}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Link className="inline-flex justify-center rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" to={card.targetPath || `/support/customers/${card.customerRefId}`}>
          상세
        </Link>
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 sm:max-w-[9rem]"
          value={currentStage}
          onChange={(e) => {
            const nextStage = e.target.value
            if (nextStage === 'MANAGEMENT_ENDED') {
              onRequestEnd?.(card)
              return
            }
            if (nextStage !== currentStage) onMove(card.customerRefId, nextStage)
          }}
        >
          {PIPELINE_CARD_STAGE_OPTIONS.map((option) => (
            <option key={option.stage} value={option.stage}>{option.label}</option>
          ))}
        </select>
      </div>
    </article>
  )
}

function ManagedCustomersModal({ data, onClose, onMove, onRequestEnd, onEnd }) {
  const customers = Array.isArray(data?.managedCustomers) ? data.managedCustomers : []
  const endedCustomers = Array.isArray(data?.managementEndedCustomers) ? data.managementEndedCustomers : []
  const [endReasonByCustomer, setEndReasonByCustomer] = useState({})
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6">
      <div className="flex max-h-[86vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-lg font-black text-slate-900">관리 고객</div>
          </div>
          <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={onClose}>
            닫기
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {customers.length === 0 ? (
            <EmptyState title="관리 상태의 고객이 없습니다." className="py-10" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {customers.map((card) => (
                <div key={card.customerRefId} className="rounded-2xl border border-slate-200 bg-white p-3">
                  <PipelineCustomerCard card={card} onMove={onMove} onRequestEnd={onRequestEnd} />
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700"
                      value={endReasonByCustomer[card.customerRefId] || ''}
                      onChange={(e) => setEndReasonByCustomer((prev) => ({ ...prev, [card.customerRefId]: e.target.value }))}
                      placeholder="관리 종료 사유"
                    />
                    <button
                      type="button"
                      className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                      onClick={() => onEnd(card.customerRefId, endReasonByCustomer[card.customerRefId] || '')}
                    >
                      관리 종료
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {endedCustomers.length > 0 ? (
            <div className="mt-5 border-t border-slate-100 pt-5">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-black text-slate-900">관리 종료 고객</div>
                <div className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{endedCustomers.length}</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {endedCustomers.map((card) => (
                  <div key={card.customerRefId} className="rounded-2xl border border-slate-200 bg-white p-3">
                    <PipelineCustomerCard card={card} onMove={onMove} onRequestEnd={onRequestEnd} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function StageMoveNotePrompt({ target, onClose, onSave }) {
  const [note, setNote] = useState('')
  useEffect(() => {
    setNote('')
  }, [target?.customerRefId, target?.stage])
  if (!target) return null
  const canSave = note.trim().length > 0
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-black text-slate-900">{target.customerName || '고객'}</div>
          <div className="mt-0.5 text-xs font-semibold text-slate-500">{target.stageLabel || pipelineStageLabel(target.stage)} 이동</div>
        </div>
        <button type="button" className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-50" onClick={onClose}>
          닫기
        </button>
      </div>
      <textarea
        className="min-h-20 w-full resize-none rounded-lg border border-slate-200 px-2.5 py-2 text-sm text-slate-700 outline-none focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={1000}
        placeholder="이동 메모"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50" onClick={onClose}>
          건너뛰기
        </button>
        <button
          type="button"
          className="rounded-lg bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:pointer-events-none disabled:opacity-40"
          disabled={!canSave}
          onClick={() => onSave(note)}
        >
          저장
        </button>
      </div>
    </div>
  )
}

function ManagementEndDialog({ target, onClose, onEnd }) {
  const [reason, setReason] = useState('')
  useEffect(() => {
    setReason('')
  }, [target?.customerRefId])
  if (!target) return null
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 px-4 py-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl">
        <div className="mb-3">
          <div className="text-base font-black text-slate-900">관리 종료</div>
          <div className="mt-1 truncate text-sm font-semibold text-slate-500">{target.customerName || '고객'}</div>
        </div>
        <textarea
          className="min-h-24 w-full resize-none rounded-lg border border-slate-200 px-2.5 py-2 text-sm text-slate-700 outline-none focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={255}
          placeholder="관리 종료 사유"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50" onClick={onClose}>
            취소
          </button>
          <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700" onClick={() => onEnd(reason)}>
            종료
          </button>
        </div>
      </div>
    </div>
  )
}

function QueueRow({ row }) {
  const title = row.title || row.customerName || row.label || '-'
  const message = row.message || row.recommendedAction || row.description || ''
  const when = row.nextMaturityDate || row.eventDate || row.startAt || row.sortAt
  const customerPath = row.targetPath || (row.customerRefId ? `/support/customers/${row.customerRefId}` : null)
  const contactPath = row.contactPath || (row.customerRefId ? `/support/customers/${row.customerRefId}#contact` : null)
  return (
    <tr className="hover:bg-slate-50/80">
      <td className="px-4 py-3 align-middle">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{categoryLabel(row.category)}</span>
          {row.severity === 'HIGH' ? (
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">중요</span>
          ) : null}
        </div>
      </td>
      <td className="max-w-[14rem] truncate px-4 py-3 align-middle font-bold text-slate-900" title={title}>
        {title}
      </td>
      <td className="max-w-[24rem] truncate px-4 py-3 align-middle text-slate-600" title={message || undefined}>
        {message || '—'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 align-middle text-xs tabular-nums text-slate-500">
        {formatDateTime(when) || '—'}
      </td>
      <td className="px-4 py-3 align-middle">
        <div className="flex justify-end gap-2">
          {customerPath ? (
            <Link className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" to={customerPath}>
              상세
            </Link>
          ) : null}
          {contactPath ? (
            <Link className="inline-flex items-center rounded-lg bg-brand-navy px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90" to={contactPath}>
              연락하기
            </Link>
          ) : null}
        </div>
      </td>
    </tr>
  )
}
